import * as Highcharts from 'highcharts';
import { EventsServiceDateRange } from '@services/events.service';
import * as moment from 'moment';
import * as _ from 'lodash';
import { SimpleChanges } from '@angular/core';
import * as html2canvas from 'html2canvas';
// import * as jsPDF from 'jspdf';
import { ConfigService, SurveyQuestionTypes } from '@services/config.service';
import { firstValueFrom } from 'rxjs';

// import Html2CanvasOptions = Html2Canvas.Html2CanvasOptions;

export interface ValueDocCountHistogramChartData {
  label: string;
  chartData: Array<any>;
  valueColorIndex: number;
}

export interface NPSInterface {
  promoters: number;
  passive: number;
  detractors: number;
  nps?: number;
  responses?: number;
  color?: string;
}

export interface ConfidenceInterface {
  sampleSize: number;
  population: number;
  marginOfError: number;   // fraction, e.g. 0.042 === ±4.2%
  confidenceLevel: number; // fraction, e.g. 0.9 for 90% — client/widget-configurable, default 0.95
  fillPct: number;         // 0-1, normalized for the confidence bar's fill height
}

export class WidgetsCommon {
  static delayBeforeChartLoad = 200;

  static saveAs(uri: string, filename: string): void {
    const link = document.createElement('a');
    if (typeof link.download === 'string') {
      link.href = uri;
      link.download = filename;
      // Firefox requires the link to be in the body
      document.body.appendChild(link);
      // simulate click
      link.click();
      // remove the link when done
      document.body.removeChild(link);
    } else {
      window.open(uri);
    }
  }

  static saveScreenShot(screenshotContent: any, title: string, type: string, additionalInfo: any): void {
    console.log('title ', title);
    // const o: Html2CanvasOptions = {
    const o: any = {
      useCORS: true,
      windowWidth: 800,
      windowHeight: 1000,
      scale: 1
    };
    // @ts-ignore
    html2canvas(screenshotContent, o).then((canvas: HTMLCanvasElement): void => {
      WidgetsCommon.saveAs(canvas.toDataURL(), `${title}.${type}`);

      // const img = canvas.toDataURL('image/png');
      //
      // let pdf;  // new jsPDF('p', 'mm', 'a4');
      // if (canvas.width > canvas.height) {
      //   pdf = new jsPDF('l', 'mm', [canvas.width, canvas.height]);
      // } else {
      //   pdf = new jsPDF('p', 'mm', [canvas.height, canvas.width]);
      // }
      // // const availableWidth = pdf.internal.pageSize.getWidth();
      // // const availableHeight = pdf.internal.pageSize.getHeight();
      // // pdf.addHTML(`<b>${title}</b>`, (): void => {});
      // // console.log('img', img);
      // pdf.addImage(img, 'PNG', 10, 20);
      // pdf.save('survey-result.pdf');
    });
  }

  /**
   * Get the common highcharts config
   * @returns Highcharts.Options
   */
  static getHighChartsConfig(): Highcharts.Options {
    return ConfigService.getCommonHighChartOptions();
  }

  /**
   * Get the common pie chart options
   * @returns Highcharts.Options
   */
  static getCommonPieChartOptions(): any {
    const commonOptions: Highcharts.Options = WidgetsCommon.getHighChartsConfig();
    const customOptions: Highcharts.Options = {
      chart: {
        type: 'pie'
      },
      boost: {
        // debug: {
        //   timeRendering: true
        // },
        useGPUTranslations: true,
        seriesThreshold: 2,
      },
      title: { floating: true, text: '' },
      navigation: {
        buttonOptions: {
          enabled: false
        }
      },
      tooltip: {
        pointFormat: '<b>{point.display}</b>'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '{point.display}',
            style: {
              // color: (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black'
              color: 'black'
            }
          },
          showInLegend: false
        },
      },
    };
    return _.merge(commonOptions, customOptions);
  }

  /**
   * Get the common summary chart options
   * @returns Highcharts.Options
   */
  static getCommonSummaryChartOptions(): any {
    const commonOptions: Highcharts.Options = WidgetsCommon.getHighChartsConfig();
    const customOptions: Highcharts.Options = {
      chart: {
        type: 'area'
      },
      title: { floating: true, text: '' },
      xAxis: [{ crosshair: true, type: 'datetime' }],
      yAxis: {
        title: {
          useHTML: true,
          text: 'Surveys Received'
        }
      },
      navigation: {
        buttonOptions: {
          enabled: false
        }
      },

      plotOptions: {
        area: {
          stacking: 'normal',
          lineColor: '#666666',
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: '#666666'
          }
        }
      },
    };
    return _.merge(commonOptions, customOptions);
  }

  /**
   * Get the common campaign summary chart options
   * @returns Highcharts.Options
   */
  static getCommonCampaignSummaryChartOptions(): any {
    const commonOptions: Highcharts.Options = WidgetsCommon.getHighChartsConfig();
    const customOptions: Highcharts.Options = {
      chart: {
        type: 'area'
      },
      title: { floating: true, text: '' },
      xAxis: [{ crosshair: true, type: 'datetime' }],
      yAxis: {
        title: {
          useHTML: true,
          text: 'Surveys Received'
        }
      },
      navigation: {
        buttonOptions: {
          enabled: false
        }
      },
    };
    return _.merge(commonOptions, customOptions);
  }

  /**
   * Configure highcharts
   */
  static configureHighCharts(): void {
    const Boost = require('highcharts/modules/boost');
    const noData = require('highcharts/modules/no-data-to-display');
    const More = require('highcharts/highcharts-more');
    Boost(Highcharts);
    noData(Highcharts);
    More(Highcharts);
    // // @ts-ignore
    // Highcharts.addEvent = function (el, type, fn, options) {
    //   if (options === void 0) {
    //     options = {};
    //   }
    //   /* eslint-enable valid-jsdoc */
    //   // tslint:disable-next-line:prefer-const
    //   // @ts-ignore
    //   var events, addEventListener = (el.addEventListener || Highcharts.addEventListenerPolyfill);
    //   // If we're setting events directly on the constructor, use a separate
    //   // collection, `protoEvents` to distinguish it from the item events in
    //   // `hcEvents`.
    //   if (typeof el === 'function' && el.prototype) {
    //     events = el.prototype.protoEvents = el.prototype.protoEvents || {};
    //   } else {
    //     events = el.hcEvents = el.hcEvents || {};
    //   }
    //   // Allow click events added to points, otherwise they will be prevented by
    //   // the TouchPointer.pinch function after a pinch zoom operation (#7091).
    //   if (Highcharts.Point &&
    //     el instanceof Highcharts.Point &&
    //     el.series &&
    //     el.series.chart) {
    //     // @ts-ignore
    //     el.series.chart.runTrackerClick = true;
    //   }
    //   // Handle DOM events
    //   if (addEventListener) {
    //     addEventListener.call(el, type, fn, {passive: true, capture: false});
    //   }
    //   if (!events[type]) {
    //     events[type] = [];
    //   }
    //   // tslint:disable-next-line:prefer-const
    //   var eventObject = {
    //     fn: fn,
    //     order: typeof options.order === 'number' ? options.order : Infinity
    //   };
    //   events[type].push(eventObject);
    //   // Order the calls
    //   events[type].sort(function (a, b) {
    //     return a.order - b.order;
    //   });
    //   // Return a function that can be called to remove this event.
    //   return function () {
    //     Highcharts.removeEvent(el, type, fn);
    //   };
    // };
  }

  /**
   * Get the previous time period
   * @param currentTimePeriod - The current time period
   * @returns EventsServiceDateRange
   */
  static getPreviousTimePeriod(currentTimePeriod: EventsServiceDateRange): EventsServiceDateRange {
    // IMPORTANT: toISOString is important because currentTimePeriod.startDate is of type dayJs which if passed as is translates into current date
    let startDate: moment.Moment = moment(currentTimePeriod.startDate.toISOString());
    // end date becomes the new start date
    const endDate: moment.Moment = moment(currentTimePeriod.startDate.toISOString());
    const duration: number = currentTimePeriod.endDate.diff(currentTimePeriod.startDate);
    // and start date is start date minus the diff between start and end date
    // console.log('currentTimePeriod', currentTimePeriod.startDate.toISOString(), currentTimePeriod.endDate.toISOString(), duration);
    startDate = moment(startDate.subtract(duration, 'milliseconds').toISOString());
    // console.log('getPreviousTimePeriod', startDate.toISOString(), endDate.toISOString(), duration);
    return new EventsServiceDateRange(startDate, endDate);
  }

  // Excludes visually-ambiguous characters (0/O, 1... actually keeps 1 but drops l/I) — same
  // alphabet convention as the url-shortener's Base58 codes (biz/url-shortener.js), just with
  // fresh random picks instead of counter-encoding.
  private static readonly SHORT_ID_ALPHABET = '123456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';

  /**
   * Generates a short, URL-safe, random widget id (e.g. for the external API's widgetId param),
   * regenerating until it doesn't collide with any id already in use on the same dashboard.
   * @param existingIds - apiIds already assigned to other widgets on this dashboard
   * @param length - number of characters to generate
   * @returns string
   */
  static generateShortWidgetId(existingIds: string[] = [], length = 3): string {
    let id: string;
    do {
      id = '';
      for (let i = 0; i < length; i++) {
        id += this.SHORT_ID_ALPHABET[Math.floor(Math.random() * this.SHORT_ID_ALPHABET.length)];
      }
    } while (existingIds.includes(id));
    return id;
  }

  /**
   * Get the NPS questions list
   * @param view - The view
   * @returns number[]
   */
  static getNPSQuestionsList(view: any): number[] {
    let questionIds = [];
    _.each(view.surveys, s => {
      if (s.surveyJSON) {
        questionIds = questionIds.concat(
          _.map(s.surveyJSON.questions, q =>
            q.questionType === 'Net Promoter Score' ? Number(q.questionId) : undefined
          )
        );
      }
    }
    );
    questionIds = _.compact(questionIds);
    return questionIds;
  }

  /**
   * Get the NPS questions list by surveys
   * @param view - The view
   * @param surveyIds - The survey IDs
   * @returns number[]
   */
  static getNPSQuestionsListBySurveys(view: any, surveyIds: number[]): number[] {
    let questionIds = [];
    _.each(view.surveys, s => {
      if (s.surveyJSON && surveyIds.includes(s.id)) {
        questionIds = questionIds.concat(
          _.map(s.surveyJSON.questions, q =>
            q.questionType === 'Net Promoter Score' ? Number(q.questionId) : undefined
          )
        );
      }
    }
    );
    questionIds = _.compact(questionIds);
    return questionIds;
  }

  /**
   * Get the selected surveys for question widget
   * @param widgetData - The widget data
   * @param view - The view
   * @returns any[]
   */
  static getSelectedSurveysForQuestionWidget(widgetData: any, view: any): any[] {
    let selectedSurveys = [];
    if (widgetData.applySurveyFilter === false) {
      selectedSurveys = _.get(view, 'surveys');
    } else {
      selectedSurveys = widgetData.selectedSurveys || _.get(view, 'surveys');
    }
    return selectedSurveys;
  }

  /**
   * Get the selected surveys
   * @param widgetData - The widget data
   * @param view - The view
   * @param selectSurveysWithNPS_Q - The select surveys with NPS question
   * @returns number[]
   */
  static getSelectedSurveys(widgetData: any, view: any, selectSurveysWithNPS_Q = false): number[] {
    let selectedSurveyIds: Array<number> = [];
    if (widgetData.selectedDashboardSurveys && widgetData.selectedDashboardSurveys.length > 0
      && widgetData.applySurveyFilter === true) {
      selectedSurveyIds = _.map(widgetData.selectedDashboardSurveys, 'id');
    } else {
      if (selectSurveysWithNPS_Q) {
        const surveyIds = [];
        _.each(view.surveys, survey => {
          const npsQuestionIds = _.compact(
            _.map(survey.surveyJSON.questions, q =>
              q.questionType === 'Net Promoter Score' ? q.questionId : undefined
            )
          );
          if (npsQuestionIds && npsQuestionIds.length > 0) {
            surveyIds.push(survey.id);
          }
          survey.npsQuestionsCount = npsQuestionIds.length;
        });
        selectedSurveyIds = _.map(_.uniq(surveyIds), Number);
      } else {
        // rely on the surveyList from the server
        selectedSurveyIds = _.map(_.uniq(view.surveyList.split(',')), Number);
      }
    }
    // console.log('selectedSurveyIds', selectedSurveyIds);
    return selectedSurveyIds;
  }

  /**
   * Get the X axis categories labels
   * @param firstDate - The first date
   * @param interval - The interval
   * @param dataLength - The data length
   * @returns any
   */
  static getXAxisCategoriesLabels(firstDate: Date, interval: string, dataLength: number): any {
    // console.log('firstDate', firstDate);
    const o: any = { xAxis: {}, series: {} };
    if (interval.toLowerCase() === '1m') {
      o.xAxis.categories = [];
      for (let i = 0; i < dataLength; i++) {
        const fdCopy: moment.Moment = moment(firstDate);
        fdCopy.add(i + 1, 'M');
        o.xAxis.categories.push(fdCopy.format('MMM'));
      }
    } else {
      o.series.pointInterval = 24 * 3600 * 1000; // one day
      if (interval.toLowerCase() === '1w') {
        o.series.pointInterval = o.series.pointInterval * 7;
      }
      o.xAxis.dateTimeLabelFormats = { week: '%e %b' };
      o.xAxis.type = 'datetime';
      o.series.pointStart = Date.UTC(firstDate.getFullYear(), firstDate.getMonth(), firstDate.getDate());

    }
    // console.log('getXAxisCategoriesLabels', o);
    return o;
  }

  /**
   * Process the histogram data
   * @param response - The response
   * @param valueField - The value field
   * @param isNPS - The is NPS
   * @returns any[]
   */
  static processHistogramData(response: any, valueField: string, isNPS: boolean): any[] {
    // console.log('response', response.body);
    const indexData = _.get(response, 'body.aggregations.histogram.buckets');
    if (!indexData) {
      return; // show some error
    }
    const chartData = indexData.map(d =>
    ({
      x: new Date(d.key),
      y: d[valueField].value === null ? 0 :
        (isNPS ? (d[valueField].value / 10) * 100 : d[valueField].value),
      doc_count: d.doc_count
    })
    );
    // console.log('chartData', chartData);
    return chartData;
  }

  /**
   * Get the line chart highcharts options
   * @param series - The series
   * @param xAxisCategories - The x axis categories
   * @returns any
   */
  static getLineChartHighChartOptions(series: any, xAxisCategories: any): any {
    const commonOptions = WidgetsCommon.getHighChartsConfig();
    const customOptions: any = {
      chart: {
        type: 'spline'
      },
      xAxis: {
        categories: xAxisCategories,
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: false
          }
        }
      },
      series
    };
    return _.merge(commonOptions, customOptions);
  }

  /**
   * Get the completion histogram options
   * @returns any
   */
  static getCompletionHistogramOptions(): any {
    const commonOptions = WidgetsCommon.getHighChartsConfig();
    const customOptions: any = {
      chart: { type: 'column' },
      title: { text: '' },
      xAxis: [{ crosshair: true, type: 'datetime' }],
      plotOptions: {
        area: {
          // fillColor: Highcharts.getOptions().colors[2],
          // fillOpacity: 0.7,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'justify',
            format: '{point.y:.0f}'
          }
        },
        column: {
          pointWidth: 15,
          dataLabels: { enabled: true, format: '{point.y}' }
        },
      },
      yAxis: [
        {
          labels: { format: '{value}' },
          title: {
            text: '',
            style: { color: Highcharts.getOptions().colors[1] }
          },
        }
      ],
      // legend: {
      //   layout: 'vertical',
      //   align: 'left',
      //   x: 50,
      //   verticalAlign: 'top',
      //   y: 0,
      //   floating: true,
      //   backgroundColor:
      //     Highcharts.defaultOptions.legend.backgroundColor || // theme
      //     'rgba(255,255,255,0.25)'
      // },
      series: [
        {
          name: 'Requests',
          color: Highcharts.getOptions().colors[0],
        },
        {
          name: 'Completions',
          color: Highcharts.getOptions().colors[1],
        }
      ]
    };
    return _.merge(commonOptions, customOptions);
  }

  /**
   * Get the index bar chart highcharts options
   * @param data - The data
   * @param interval - The interval
   * @param data2 - The data2
   * @param docCountColorIndexDarkenByPct - The doc count color index darken by percent
   * @returns any
   */
  static getIndexBarChartHighChartOptions(
    data: ValueDocCountHistogramChartData,
    interval: string,
    data2: ValueDocCountHistogramChartData = undefined,
    docCountColorIndexDarkenByPct = 36): any {
    _.defaults(data.valueColorIndex, 0);
    const commonOptions = WidgetsCommon.getHighChartsConfig();
    const seriesColor = Highcharts.getOptions().colors[data.valueColorIndex];
    // @ts-ignore
    const secondaryYAxisMax: number = _.max(_.flatten(_.map(data.chartData, 'doc_count'), _.map(data2, 'doc_count'))) * 4;
    // const customOptions: Highcharts.Options = {
    const customOptions: any = {
      chart: { zoomType: 'xy', type: 'area' },
      title: { text: '' },
      xAxis: [{ crosshair: true }],
      plotOptions: {
        area: {
          // fillColor: Highcharts.getOptions().colors[2],
          // fillOpacity: 0.7,
          marker: {
            enabled: false,
            symbol: 'circle',
            radius: 2,
            states: { hover: { enabled: true } }
          },
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'justify',
            format: '{point.y:.0f}'
          }
        },
        column: {
          pointWidth: 15,
          dataLabels: { enabled: true, format: '{point.y}' }
        },
      },
      yAxis: [
        {
          labels: { format: '{value}' },
          title: {
            text: 'NPS',
            style: { color: Highcharts.getOptions().colors[1] }
          },
        },
        { // Secondary yAxis
          max: secondaryYAxisMax,
          gridLineWidth: 0,
          title: {
            text: '# Responses',
            // style: {
            //   color: Highcharts.getOptions().colors[0]
            // }
          },
          labels: {
            format: '{value}',
            style: {
              color: Highcharts.getOptions().colors[0]
            }
          },
          visible: false,
          opposite: true
        }
      ],
      tooltip: {
        shared: true,
        // valueDecimals: 1
      },
      legend: {
        layout: 'vertical',
        align: 'left',
        x: 50,
        verticalAlign: 'top',
        y: 0,
        floating: true,
        backgroundColor:
          Highcharts.defaultOptions.legend.backgroundColor || // theme
          'rgba(255,255,255,0.25)'
      },
      series: [
        {
          name: data.label,
          type: 'area',
          color: seriesColor,
          data: _.map(_.map(data.chartData, 'y'), v => Number(v.toFixed(1))),
        },
        {
          name: data2 ? `${data.label} (responses)` : 'Responses',
          yAxis: 1,
          type: 'column',
          format: '{value}',
          pointWidth: 10,
          data: _.map(data.chartData, 'doc_count'),
          color: this.shadeColor(seriesColor, (-1 * docCountColorIndexDarkenByPct)),
        }
      ]
    };
    const highChartOptions = _.merge(commonOptions, customOptions);
    if (data2) {
      const seriesColor2 = Highcharts.getOptions().colors[data2.valueColorIndex];
      const mainSeries = {
        name: data2.label,
        type: 'area',
        color: seriesColor2,
        data: _.map(_.map(data2.chartData, 'y'), v => Number(v.toFixed(1))),
      };
      const docCountSeries = {
        name: `${data2.label} (responses)`,
        yAxis: 1,
        type: 'column',
        format: '{value}',
        pointWidth: 10,
        data: _.map(data2.chartData, 'doc_count'),
        color: this.shadeColor(seriesColor2, (-1 * docCountColorIndexDarkenByPct)),
      };
      highChartOptions.series.push(mainSeries);
      highChartOptions.series.push(docCountSeries);
    }
    const xAxisCategoryLabels =
      WidgetsCommon.getXAxisCategoriesLabels(data.chartData[0].x, interval, data.chartData.length);

    highChartOptions.xAxis[0] = _.merge(highChartOptions.xAxis[0], xAxisCategoryLabels.xAxis);
    highChartOptions.series[0] = _.merge(highChartOptions.series[0], xAxisCategoryLabels.series);
    highChartOptions.series[1] = _.merge(highChartOptions.series[1], xAxisCategoryLabels.series);
    if (data2) {
      highChartOptions.series[2] = _.merge(highChartOptions.series[2], xAxisCategoryLabels.series);
      highChartOptions.series[3] = _.merge(highChartOptions.series[3], xAxisCategoryLabels.series);
    }
    // console.log('highChartOptions', highChartOptions);

    return highChartOptions;
  }

  /**
   *
   * @param color hexColor
   * @param darkenByPercent darkenBy Percentage, give -ve number to lighten
   */
  static shadeColor(color: string, darkenByPercent: number): string {

    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);

    R = parseInt((R * (100 + darkenByPercent) / 100).toString(), 10);
    G = parseInt((G * (100 + darkenByPercent) / 100).toString(), 10);
    B = parseInt((B * (100 + darkenByPercent) / 100).toString(), 10);

    R = (R < 255) ? R : 255;
    G = (G < 255) ? G : 255;
    B = (B < 255) ? B : 255;

    // eslint-disable-next-line eqeqeq
    const RR = ((R.toString(16).length == 1) ? '0' + R.toString(16) : R.toString(16));
    // eslint-disable-next-line eqeqeq
    const GG = ((G.toString(16).length == 1) ? '0' + G.toString(16) : G.toString(16));
    // eslint-disable-next-line eqeqeq
    const BB = ((B.toString(16).length == 1) ? '0' + B.toString(16) : B.toString(16));
    return '#' + RR + GG + BB;
  }

  /**
   * Resize the chart
   * @param chart - The chart
   * @param changes - The changes
   * @param currentWidgetSize - The current widget size
   * @param shrinkWidth - The shrink width
   * @param shrinkHeight - The shrink height
   * @param forceWidth - The force width
   * @param forceHeight - The force height
   * @param table 
   * @returns 
   */
  static resizeChart(chart: any, changes: SimpleChanges, currentWidgetSize: any, shrinkWidth = 20, shrinkHeight = 70,
    forceWidth = -1, forceHeight = -1, table = null): any {
    // console.log('resizeChart', changes.mySize);
    if (changes.mySize) {
      const widgetSize = { height: 0, width: 0 };
      if (changes.mySize.currentValue && changes.mySize.currentValue.height > 0) {
        widgetSize.height = forceHeight > -1 ? forceHeight : changes.mySize.currentValue.height - shrinkHeight;
        widgetSize.width = forceWidth > -1 ? forceWidth : changes.mySize.currentValue.width - shrinkWidth;
        if (table) {
          const datatableElement = table.element;
          const parentElement = datatableElement.parentElement;
          if (parentElement) {
            parentElement.style.height = (widgetSize.height) + 'px'; // Adjust height dynamically
            parentElement.style.width = (widgetSize.width) + 'px'; // Adjust width dynamically
            table.recalculate();
          }
        } else if (chart) {
          chart.setSize(widgetSize.width, widgetSize.height);
          chart.reflow();
        }
      }
      return widgetSize;
    } else {
      return currentWidgetSize;
    }
  }

  /**
   * Get the NPS
   * @param nps - The NPS
   * @returns NPSInterface
   */
  static getNPS(nps: NPSInterface): NPSInterface {
    const total = nps.detractors + nps.promoters + nps.passive;
    if (total === 0) {
      nps.nps = 0;
      nps.color = '#a7aaac';
      return nps;
    }
    const npsVal: number = ((100 * nps.promoters) / total) - ((100 * nps.detractors) / total);
    nps.nps = parseFloat(npsVal.toFixed(1));
    const npsColors = ConfigService.getNumericQuestionColors();
    nps.color = (nps.nps > 20) ? npsColors[0] : (nps.nps > -20 ? npsColors[1] : npsColors[2]);
    return nps;
  }

  // UI-normalization thresholds for the NPS confidence bar — a presentation concern, kept separate
  // from the backend's raw margin-of-error math so they can be tuned without a backend deploy.
  static readonly CONFIDENCE_BAR_MOE_FULL = 0.03;  // MoE <= 3%  -> 100% fill
  static readonly CONFIDENCE_BAR_MOE_EMPTY = 0.15; // MoE >= 15% -> 0% fill

  /**
   * Normalizes a margin of error into a 0-1 fill percentage for the confidence bar.
   * @param marginOfError - Margin of error as a fraction (e.g. 0.042), or null/undefined
   * @returns number - 0 (least confident) to 1 (most confident)
   */
  static getConfidenceFillPct(marginOfError: number): number {
    if (marginOfError === null || marginOfError === undefined || isNaN(marginOfError)) {
      return 0;
    }
    const full = WidgetsCommon.CONFIDENCE_BAR_MOE_FULL;
    const empty = WidgetsCommon.CONFIDENCE_BAR_MOE_EMPTY;
    if (marginOfError <= full) {
      return 1;
    }
    if (marginOfError >= empty) {
      return 0;
    }
    return 1 - ((marginOfError - full) / (empty - full));
  }

  /**
   * Extracts the confidence indicator fields from a before-after-by-NPS or before-after-by-tag
   * API response (both attach the same sampleSize/population/marginOfError/confidence fields
   * when the caller passed a resultType).
   * @param response - The raw HTTP response from getNPSMetricsData / getFollowUpByTag
   * @returns ConfidenceInterface, or null when the backend didn't compute confidence data
   */
  static getConfidenceFromResponse(response: any): ConfidenceInterface {
    const body = response.body;
    if (body.marginOfError === undefined || body.marginOfError === null) {
      return null;
    }
    return {
      sampleSize: body.sampleSize,
      population: body.population,
      marginOfError: body.marginOfError,
      confidenceLevel: body.confidence,
      fillPct: WidgetsCommon.getConfidenceFillPct(body.marginOfError)
    };
  }

  // Confidence bar fill colors: green once fillPct crosses the configurable "high confidence"
  // threshold (ConfigService.config.confidenceBarHighThresholdPct), grey below it.
  static readonly CONFIDENCE_BAR_COLOR_HIGH = '#2ca02c';
  static readonly CONFIDENCE_BAR_COLOR_LOW = '#616161';

  /**
   * Single source of truth for the confidence bar's "high vs. not" state — both the bar's color
   * and its caption's text label derive from this, so they can never disagree with each other.
   * @param fillPct - 0-1, as produced by getConfidenceFillPct()
   */
  static isHighConfidence(fillPct: number): boolean {
    const thresholdPct = ConfigService.config.confidenceBarHighThresholdPct ?? 80;
    return (fillPct || 0) * 100 >= thresholdPct;
  }

  /**
   * Picks the confidence bar's fill color based on how its fillPct compares to the configurable
   * high-confidence threshold, so the threshold can be tuned in ConfigService without touching
   * any widget component.
   * @param fillPct - 0-1, as produced by getConfidenceFillPct()
   */
  static getConfidenceBarColor(fillPct: number): string {
    return WidgetsCommon.isHighConfidence(fillPct) ? WidgetsCommon.CONFIDENCE_BAR_COLOR_HIGH : WidgetsCommon.CONFIDENCE_BAR_COLOR_LOW;
  }

  /**
   * Short qualitative label for the confidence caption — deliberately NOT a percentage: the
   * confidence *level* (see ConfidenceInterface.confidenceLevel) is a configured policy choice
   * that doesn't vary with sample size, so displaying it as the headline number next to a bar
   * whose color DOES change with sample size was misleading (a grey/low bar sitting next to a
   * constant "95%"/"90%"/etc.). This label uses the exact same threshold as the bar's color, so
   * the words and the color always agree.
   * @param fillPct - 0-1, as produced by getConfidenceFillPct()
   */
  static getConfidenceTierLabel(fillPct: number): string {
    return WidgetsCommon.isHighConfidence(fillPct) ? 'High confidence' : 'Limited confidence';
  }

  // The population-basis dropdown options shown in widget Settings dialogs (Response Rate, NPS,
  // Index Metrics, ...) — single source of truth so every widget offers the exact same choices.
  static readonly POPULATION_BASIS_OPTIONS: Array<{ name: string; title: string }> = [
    { name: 'impressions', title: 'Survey Impressions (when using Always On)' },
    { name: 'campaign', title: 'Campaign (when using Campaign Completions)' },
    { name: 'default', title: 'Counts Campaigns and Impressions both' },
  ];

  // The confidence-level preset dropdown options shown in the Client Settings admin page and in
  // widget Settings dialogs (NPS Metrics, Index Metrics) — single source of truth.
  static readonly CONFIDENCE_LEVEL_OPTIONS: Array<{ name: string; title: string }> = [
    { name: '80', title: '80%' },
    { name: '85', title: '85%' },
    { name: '90', title: '90%' },
    { name: '95', title: '95% (default)' },
    { name: '99', title: '99%' },
  ];

  // Labels for whichever population basis a widget's Settings dialog has selected
  // (widgetData.chartType), used to explain what a confidence bar's "N" actually counts.
  static readonly POPULATION_BASIS_LABELS: { [key: string]: string } = {
    impressions: 'survey impressions',
    campaign: 'campaign requests',
    default: 'campaign requests, or impressions if this is an Always On survey'
  };

  // Dashboard names matching this signal an Always On / Career Site style dashboard — those
  // surveys are evergreen (no discrete campaign sends), so impressions are the natural population.
  private static readonly ALWAYS_ON_DASHBOARD_NAME_PATTERN = /always on|career site/i;

  /**
   * Computes the population basis a confidence-bar widget should use when it has no explicit
   * chartType override: 'impressions' for an Always On / Career Site dashboard (by name), 'campaign'
   * otherwise. This is the fallback used in place of the widget's own unset chartType — it does
   * NOT affect a widget where the user explicitly chose a population basis (including the literal
   * "both" option), only what happens when nothing has been chosen yet.
   * @param dashboardTitle - The current dashboard's title, if available (e.g. undefined outside a
   *   real dashboard view, such as a per-survey or insights context) — treated as no match.
   */
  static getDefaultPopulationBasis(dashboardTitle: string): string {
    return WidgetsCommon.ALWAYS_ON_DASHBOARD_NAME_PATTERN.test(dashboardTitle || '') ? 'impressions' : 'campaign';
  }

  /**
   * Chart type a newly added Completion widget starts with: 'default' (campaigns and impressions both)
   * for an Always On / Career Site dashboard (by name), 'campaign' otherwise.
   * @param dashboardTitle - The dashboard's title, if available — treated as no match when missing.
   */
  static getDefaultCompletionChartType(dashboardTitle: string): string {
    return WidgetsCommon.ALWAYS_ON_DASHBOARD_NAME_PATTERN.test(dashboardTitle || '') ? 'default' : 'campaign';
  }

  /**
   * Describes what the margin of error actually means for THIS score, based on where its
   * fillPct lands relative to the confidence bar's own normalization tiers
   * (CONFIDENCE_BAR_MOE_FULL/EMPTY), rather than a one-size-fits-all sentence.
   */
  static getMarginOfErrorExplanation(c: ConfidenceInterface): string {
    if (c.fillPct >= 0.66) {
      return 'This margin of error is small, so this score is unlikely to shift much as more responses come in.';
    } else if (c.fillPct >= 0.33) {
      return 'This margin of error is moderate, so this score could shift somewhat as more responses come in.';
    } else {
      return 'This margin of error is large, so this score could change significantly as more responses come in ' +
        '— treat it as a rough estimate for now.';
    }
  }

  /**
   * Builds the multi-line confidence-bar tooltip text (use with matTooltipClass="multiline-tooltip").
   * @param c - The confidence indicator fields for this widget instance
   * @param chartType - The population basis actually being used ('impressions'|'campaign'|'default'),
   *   already resolved from widgetData.chartType || the computed dashboard default — drives the
   *   population label shown.
   * @param responseNoun - Singular noun for what was sampled, e.g. 'NPS response' or 'response'
   * @param hasExplicitPopulationBasis - Whether the WIDGET itself has an explicit chartType set
   *   (as opposed to `chartType` being a computed default) — controls whether to nudge the user
   *   toward Settings. Defaults to true (no nudge) for any caller not yet passing this.
   */
  static getConfidenceTooltip(c: ConfidenceInterface, chartType: string, responseNoun = 'response',
    hasExplicitPopulationBasis = true): string {
    const resolvedChartType = chartType || 'default';
    const populationLabel = WidgetsCommon.POPULATION_BASIS_LABELS[resolvedChartType];
    const responseWord = c.sampleSize === 1 ? responseNoun : `${responseNoun}s`;
    let explanation = WidgetsCommon.getMarginOfErrorExplanation(c);
    if (!hasExplicitPopulationBasis) {
      // Only nudge toward Settings while the user hasn't already made an explicit choice.
      explanation += ' Change the population basis in Settings.';
    }
    return [
      // Leads with the tier label (which tracks the bar's color) and the varying margin of
      // error, not the fixed 95% CI level — that number never changes with sample size, so
      // leading with it next to a bar whose color DOES change was misleading. The 95% is still
      // named, just as supporting detail for the margin-of-error figure, not the headline.
      `${WidgetsCommon.getConfidenceTierLabel(c.fillPct)} — margin of error ±${(c.marginOfError * 100).toFixed(1)}% ` +
        `(at ${(c.confidenceLevel * 100).toFixed(0)}% confidence)`,
      '',
      `Based on ${c.sampleSize} ${responseWord} out of an estimated population of ${c.population} ` +
        `(${populationLabel}).`,
      '',
      explanation
    ].join('\n');
  }

  /**
   * Get the high low question chart options
   * @param categories - The categories
   * @returns any
   */
  static getHighLowQuestionChartOptions(categories: any): any {
    return {
      chart: {
        type: 'column',
      },
      // colors: this.category20Colors,
      xAxis: {
        categories
      },
      legend: { enabled: false },
      yAxis: {
        min: 0,
        title: { text: '' }
      },
      tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: '<tr><td style="color:{series.color};padding:0">{point.percent}% - {point.y}</td></tr>',
        footerFormat: '</table>',
        shared: true,
        useHTML: true
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'none',
            formatter() {
              // console.log(this);
              return `${this.point.percent}%`;
            }
          }
        }
      }
    };
  }

  static getNPSJourneyChartOptions(categories: any): any {
    return {
      chart: {
        type: 'bar',
      },
      // colors: this.category20Colors,
      xAxis: {
        categories
      },
      legend: { enabled: false },
      yAxis: {
        min: -100,
        max: 100,
        title: { text: 'NPS' }
      },
      tooltip: {
        // valueSuffix: ' (NPS)'
        formatter(): string {
          return `<b>${this.y} (NPS) </b><br/>${this.point.myLabel ? WidgetsCommon.shortenNumber(this.point.myLabel) + ' responses' : '0'}`;
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'none',
            formatter() {
              return `${this.y}<br/>${this.point.myLabel ? WidgetsCommon.shortenNumber(this.point.myLabel) : ''}`;
            }
          },
          groupPadding: 0.1
        }
      }
    };
  }

  /**
   * Breakdown colors used by the competitor NPS tooltip and NPS series - matches nps.component.ts baseColors
   */
  static readonly competitorNPSBreakdownColors: { detractors: string; passive: string; promoters: string } = {
    detractors: '#d62728',
    passive: '#1f77b4',
    promoters: '#2ca02c'
  };

  /**
   * Chart options for the competitor NPS widget - one diverging bar per competitor, bar length is the NPS score.
   * @param categories - The competitor names
   * @returns any
   */
  static getCompetitorNPSChartOptions(categories: any): any {
    return {
      chart: {
        type: 'bar',
      },
      xAxis: {
        categories,
        lineWidth: 0,
        tickWidth: 0
      },
      legend: { enabled: false },
      yAxis: {
        min: -100,
        max: 100,
        tickInterval: 50,
        title: { text: 'NPS' },
        plotLines: [{ value: 0, width: 1, zIndex: 3, color: '#bdbdbd' }]
      },
      tooltip: {
        useHTML: true,
        outside: true,
        shared: false,
        backgroundColor: '#ffffff',
        borderColor: '#e0e0e0',
        borderRadius: 4,
        borderWidth: 1,
        shadow: true,
        formatter(): string {
          return WidgetsCommon.competitorNPSTooltip(this.point);
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          groupPadding: 0.1,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'none',
            useHTML: true,
            formatter() {
              return `<b>${this.y > 0 ? '+' : ''}${this.y}</b>` +
                `<span style="color:#9aa0a6;margin-left:4px">${WidgetsCommon.shortenNumber(this.point.rated)}</span>`;
            }
          }
        }
      }
    };
  }

  /**
   * HTML tooltip for the competitor NPS chart - shows the promoter/passive/detractor breakdown for a point.
   * @param point - The Highcharts point, carrying rated/promoters/passives/detractors/notRated
   * @returns string
   */
  static competitorNPSTooltip(point: any): string {
    const rated = point.rated || 0;
    const c = WidgetsCommon.competitorNPSBreakdownColors;
    const pct = (n: number): number => rated === 0 ? 0 : Math.round((n / rated) * 100);
    const seg = (n: number, color: string): string =>
      `<i style="display:block;height:100%;width:${pct(n)}%;background:${color}"></i>`;
    const row = (label: string, color: string, n: number): string =>
      `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color}"></span>` +
      `<span>${label}</span>` +
      `<b style="text-align:right">${n}</b>` +
      `<span style="color:#9aa0a6;text-align:right">${pct(n)}%</span>`;

    return `<div style="font-family:Roboto,system-ui,sans-serif;min-width:190px;background:#ffffff">` +
      `<div style="font-weight:500;font-size:13px;margin-bottom:1px">${point.category}</div>` +
      `<div style="font-size:11.5px;color:#5f6368;margin-bottom:7px">` +
        `NPS <b style="font-size:13px;color:#212121">${point.y > 0 ? '+' : ''}${point.y}</b> · ${rated} rated` +
        `${point.notRated ? ' · ' + point.notRated + ' not rated' : ''}` +
      `</div>` +
      `<div style="display:flex;height:9px;border-radius:2px;overflow:hidden;gap:2px;margin-bottom:7px">` +
        seg(point.detractors, c.detractors) +
        seg(point.passives, c.passive) +
        seg(point.promoters, c.promoters) +
      `</div>` +
      `<div style="display:grid;grid-template-columns:auto 1fr auto auto;column-gap:7px;row-gap:2px;font-size:11.5px;color:#5f6368;align-items:center">` +
        row('Promoters', c.promoters, point.promoters) +
        row('Passives', c.passive, point.passives) +
        row('Detractors', c.detractors, point.detractors) +
      `</div>` +
    `</div>`;
  }

  /**
   * Destroy the high chart
   * @param instance - The instance
   */
  static destroyHighChart(instance: any): void {
    try {
      instance.destroy();
    } catch (e) {
    }
  }

  /**
   * Get the normalized value
   * @param val - The value
   * @param scale - The scale
   * @returns number
   */
  static getNormalizedVal(val: number | string, scale: number): number {

    if (scale === 3) {
      switch (val) {
        case 0:
          return 3; // 33%
          break;
        case 1:
          return 7; // 66%
          break;
        case 2:
          return 10;
          break;
        case 'default':
          break;
      }
    } else if (scale === 5) {
      switch (val) {
        case 0:
          return 2;
          break;
        case 1:
          return 4;
          break;
        case 2:
          return 6;
          break;
        case 3:
          return 8;
          break;
        case 4:
          return 10;
          break;
        case 'default':
          break;
      }
    } else {
      if (typeof val === 'string') {
        val = parseInt(val, 10);
      }
      return val + 1;
    }
  };


  /**
   * Map on ten pt scale
   * @param scale - The scale
   * @param value - The value
   * @param inverse - The inverse
   * @returns number[]
   */
  static mapOnTenPtScale(scale: number, value: number | string, inverse: boolean = false): number[] {
    let realIdxMap;
    // there is a weird logic here
    // number that is sent back is actually the position of the zero based array - 1
    // so if 8 is returned the  8 + 1st position in a zero based array, meaning key of 10
    switch (scale) {
      case 10:
        return [Number(value)];
      case 5:
        realIdxMap = { new: { 0: 0, 1: 2, 2: 4, 3: 6, 4: 8 }, old: { 0: -1, 1: 1, 2: 3, 3: 5, 4: 7 } };
        break;
      case 4:
        realIdxMap = { new: { 0: 2, 1: 4, 2: 7, 3: 9 }, old: { 0: 2, 1: 4, 2: 7, 3: 9 } };
        break;
      case 3:
        realIdxMap = { new: { 0: 1, 1: 5, 2: 8 }, old: { 0: -1, 1: 3, 2: 8 } };
        break;
    }
    if (inverse) {
      const val: number = ((typeof value === 'string') ? parseInt(value, 10) : value) - 1;
      const o = _.find(realIdxMap.old, (v, k: number) => val.toString() === k.toString());
      const o2 = _.find(realIdxMap.new, (v, k: number) => val.toString() === k.toString());
      if (_.isUndefined(o)) {
        console.log('scale, value, realIdxMap', scale, value, inverse);
        throw new Error('Invalid or out of bound value');
      }
      return [Number(o) + 2, Number(o2) + 2];
    } else {
      if (scale === 3) {
        console.log('realIdxMap.new[value] || realIdxMap.old[value]', value, realIdxMap.new[value], realIdxMap.old[value]);
      }
      return [realIdxMap.new[value], realIdxMap.old[value]];
    }
    // below for 3pt scale
    // case 10:
    //   return 3;
    // case 7:
    // case 5:
    //   return 2;
    // case 1:
    // case 3:
    //   return 1;

    // below for 5pt scale
    // case 10:
    //   return 5;
    // case 7:
    // case 8:
    //   return 4;
    // case 6:
    // case 5:
    //   return 3;
    // case 4:
    // case 3:
    //   return 2;
    // case 1:
    //   return 1;
    // case 0:
    //   return 0;
  }

  /**
   * Get the un normalized value
   * @param val - The value
   * @param scale - The scale
   * @returns number
   */
  static getUnNormalizedVal(val: number | string, scale: number): number {
    if (typeof val === 'string') {
      val = parseInt(val, 10);
    }
    const keyMapInverse = {
      5: { 1: 1, 3: 2, 5: 3, 7: 4, 10: 5 },
      3: { 3: 1, 7: 2, 10: 3 }
    };
    const keyMapInverse2 = {
      5: { 2: 1, 4: 2, 6: 3, 8: 4, 10: 5 },
      3: { 3: 1, 7: 2, 10: 3 }
    };
    // return scale === 10 ? val : keyMapInverse[scale][val] || keyMapInverse2[scale][val];
    return scale === 10 ? val :
      (_.get(keyMapInverse, `${scale}.${val}`, 0) as number) +
      (_.get(keyMapInverse2, `${scale}.${val}`, 0) as number);
  }

  /**
   * Get the NPS chart highcharts options
   * @param npsData - The NPS data
   * @param previousNpsData - The previous NPS data
   * @param isBenchmarks - The is benchmarks
   * @returns Highcharts.Options
   */
  static getNPSChartHighChartOptions(npsData: NPSInterface, previousNpsData: NPSInterface, isBenchmarks: boolean,
    compareAgainstUnfiltered: boolean): Highcharts.Options {

    const commonOptions = WidgetsCommon.getHighChartsConfig();
    npsData = this.getNPS(npsData);
    previousNpsData = this.getNPS(previousNpsData);
    const colors = ConfigService.getStandardNumberScaleColors();
    const lightColors = _.map(colors, c => `${c}40`);
    const change = parseFloat(parseFloat((npsData.nps - previousNpsData.nps).toString()).toFixed(1));
    const nps = npsData.nps ? parseFloat(npsData.nps.toString()).toFixed(1) : 0;
    // const customOptions: Highcharts.Options =  {
    const customOptions: any = {
      chart: {
        plotShadow: false
      },
      title: {
        floating: true,
        useHTML: true,
        text: `<div style="font-size:27px;text-align: center;">${nps}</div>
    <div style="font-size:18px;text-align: center">` +
          (_.isNaN(change) || change === 0 ? 'No Change' : (change < 0 ? `DOWN ${Math.abs(change)}` : `UP ${change}`)) +
          '</div>' +
          (compareAgainstUnfiltered === true ? '<div style="font-size:12px;text-align: center;">from unfiltered</div>' : ''),
        // text: `${nps}<br/>` +  _.isNaN(change) || change === 0 ? '' : (change < 0 ? `DOWN ${Math.abs(change)}` : `UP ${change}`),
        align: 'center',
        verticalAlign: 'bottom',
        y: 20,
        style: { fontSize: '27px' }
      },
      subtitle: {
        floating: true,
        text: `${WidgetsCommon.shortenNumber(npsData.detractors + npsData.promoters + npsData.passive)} Responses`,
        align: 'right',
        verticalAlign: 'bottom',
        y: 20,
        style: { color: '#111', fontSize: '12px' }
      },
      tooltip: {
        formatter(): string {
          if (this.point.series.data.length === 1) {
            return '<b>' + this.point.y + '</b>';
          } else {
            return this.point.tt + ' ' + '<b>' + this.point.y + ' (' + this.point.percentage.toFixed(1) + '%)</b>';
          }
        }
        // pointFormat: '{point.tt} <b>{point.y} ({point.percentage:.1f}%)</b>' //
      },
      pane: {
        center: ['50%', '50%'],
        size: '50%',
        startAngle: -113,
        endAngle: 113,
        background: {
          borderWidth: 0,
          backgroundColor: 'none',
          innerRadius: '60%',
          outerRadius: '100%',
          shape: 'arc'
        }
      },
      yAxis: [{
        lineWidth: 0,
        min: -100,
        max: 100,
        minorTickLength: 0,
        tickLength: 0,
        tickWidth: 0,
        labels: {
          enabled: false
        },
        title: {
          text: '', // '<div class="gaugeFooter">46% Rate</div>',
          useHTML: true,
          y: 100
        },
        /* plotBands: [{
          from: 0,
          to: 46,
          color: 'pink',
          innerRadius: '100%',
          outerRadius: '0%'
        },{
          from: 46,
          to: 90,
          color: 'tan',
          innerRadius: '100%',
          outerRadius: '0%'
        }],*/
        pane: 0,

      }],
      plotOptions: {
        pie: {
          // colors,
          dataLabels: {
            enabled: false,
            distance: -20,
            style: {
              fontWeight: 'bold',
              color: 'white',
            }
          },
          startAngle: -120,
          endAngle: 120,
          center: ['50%', '50%']
        },
        gauge: {
          dataLabels: {
            enabled: false,
          },
          dial: {
            radius: '140%'
          },

        }
      },

      series: [
        {
          type: 'pie',
          name: '',
          size: '100%',
          innerSize: '70%',
          data: [
            { name: '', tt: 'Detractors ', y: npsData.detractors, color: colors[0] },
            { name: '', tt: 'Passive ', y: npsData.passive, color: colors[1] },
            { name: '', tt: 'Promoters ', y: npsData.promoters, color: colors[2] },
          ]
        },
        {
          type: 'pie',
          name: '',
          size: '69%',
          innerSize: '68.5%',
          data: [
            {
              name: '',
              tt: isBenchmarks === undefined ? (compareAgainstUnfiltered === true ? 'Unfiltered Detractors' : 'Previous Detractors') : 'Benchamrk Detractors',
              y: previousNpsData.detractors,
              color: lightColors[0]
            },
            {
              name: '',
              tt: isBenchmarks === undefined ? (compareAgainstUnfiltered === true ? 'Unfiltered Passive' : 'Previous Passive') : 'Benchamrks Passive',
              y: previousNpsData.passive,
              color: lightColors[1]

            },
            {
              name: '',
              tt: isBenchmarks === undefined ? (compareAgainstUnfiltered === true ? 'Unfiltered Promoters' : 'Previous Promoters') : 'Benchmarks Promoters',
              y: previousNpsData.promoters,
              color: lightColors[2]

            },
          ]
        },
        {
          name: 'NPS',
          type: 'gauge',
          data: [npsData.nps],
          dial: {
            rearLength: 0,
          },
        }
      ],
      labels: {
        items: [{
          html: 'Title1',
          style: {
            right: '-10%',
            bottom: '-10%',
            color: 'black'
          }
        },
          // {
          //   html: 'xxTitle2',
          //   style: {
          //     left: '400px',
          //     top: '20px',
          //     color: 'black'
          //   }
          // }
        ]
      },
    };
    const highChartOptions = _.merge(commonOptions, customOptions);
    return highChartOptions;
  }

  /**
   * Cande override question to hi lo scale
   * @param questionJSON - The question JSON
   * @param isPre2023Survey - The is pre 2023 survey
   */
  static candeOverrideQuestionToHiLoScale(questionJSON: any, isPre2023Survey: boolean): void {
    questionJSON.questionType = 'High/Low Scale';
    questionJSON.hiLo = {
      scaleOption: {
        name: 'Stars',
        id: 3
      },
      labels: [
        {
          name: 'Very dissatisfied: My experience was negative.',
          id: 1
        },
        {
          name: 'Dissatisfied.',
          id: 2
        },
        {
          name: 'Satisfied.',
          id: 3
        },
        {
          name: 'Very satisfied.',
          id: 4
        },
        {
          name: 'Delighted: My experience was positive.',
          id: 5
        }
      ],
      scale: 'Stars',
      highOption: 5,
      lowOption: 1,
      highLowOption: {
        id: 2,
        name: '1-5'
      }
    };
    // if (!isPre2023Survey) {
    //   questionJSON.hiLo.labels =  [
    //     {
    //       'name': 'Delighted: My experience was positive.',
    //       'id': 1
    //     },
    //     {
    //       'name': 'Very satisfied.',
    //       'id': 2
    //     },
    //     {
    //       'name': 'Satisfied.',
    //       'id': 3
    //     },
    //     {
    //       'name': 'Dissatisfied.',
    //       'id': 4
    //     },
    //     {
    //       'name': 'Very dissatisfied: My experience was negative.',
    //       'id': 5
    //     }
    //   ]
    //   questionJSON.hiLo.highOption = 1;
    //   questionJSON.hiLo.lowOption = 5;
    // }
    return questionJSON;
  }

  /**
   * Calculate the benchmark scores
   * @param questionJSON - The question JSON
   * @param benchmarkData - The benchmark data
   * @param trulyCande - The truly cande
   * @param parentSurveyId - The parent survey ID
   */
  static calculateBenchmarkScores(questionJSON: any, benchmarkData: any, trulyCande: boolean, parentSurveyId: string): void {
    // let benchmarkData = {};
    // let yourScore;
    // console.log('questionJSON.questionId', questionJSON.questionId, questionJSON.questionType );
    // cande special, since branching is not available on hi-low, multiple choice is used as hiLo

    const is2023Survey = [4002, 4001, 4000, 3999].indexOf(parseInt(parentSurveyId, 10)) > -1;
    const is2024Survey = [4525, 4526, 4527, 4528].indexOf(parseInt(parentSurveyId, 10)) > -1;
    // if (!is2024Survey && questionJSON.questionId === 5675 && !_.isNil(parentSurveyId)) {
    //   questionJSON.questionType = 'High/Low Scale';
    //   questionJSON.hiLo.highOption = 5;
    // }

    if ([5739, 5748].indexOf(questionJSON.questionId) > -1) {
      questionJSON = WidgetsCommon.candeOverrideQuestionToHiLoScale(questionJSON, !is2023Survey);
    }
    // if (questionJSON.questionId.toString() === '1198') {
    //   console.log('benchmarkData', benchmarkData);
    // }

    // console.log('allClients', questionJSON.questionType, benchmarkData.AllClients);
    if (questionJSON.questionType === 'Net Promoter Score') {
      const ac = benchmarkData.AllClients;
      benchmarkData.AllClientsAverage = parseFloat((((ac.promoters - ac.detractors) * 100) / ac.all).toFixed(1));
      if (!_.isEmpty(benchmarkData.winners)) {
        const namWinners = benchmarkData.winners;
        namWinners.totalCount = namWinners.promoters + namWinners.detractors + namWinners.passive;
        benchmarkData.namWinnersBenchmark =
          parseFloat((((namWinners.promoters * 100) / namWinners.totalCount) -
            ((namWinners.detractors * 100) / namWinners.totalCount)).toFixed(1));
      }

      // yourScore = w.NPSScore; // TODO
      // } else if (questionJSON.questionSubType === 'CANDE-NPS' && questionJSON.bothClientSourceSame === true) {
    } else if (questionJSON.questionSubType === 'CANDE-NPS') {
      // calculate funky NPS
      // same name , but storing NPS score
      const allClients = benchmarkData.AllClients;
      benchmarkData.AllClientsAverage =
        parseFloat(((((allClients.promoters * 100) / allClients.totalCount) -
          ((allClients.detractors * 100) / allClients.totalCount))).toFixed(1));

      if (!_.isEmpty(benchmarkData.winners)) {
        const namWinners = benchmarkData.winners;
        benchmarkData.namWinnersBenchmark =
          parseFloat((((namWinners.promoters * 100) / namWinners.totalCount) -
            ((namWinners.detractors * 100) / namWinners.totalCount)).toFixed(1));
      }
    } else {
      let all; let winner; let totalSum; let totWin;
      if (questionJSON.questionType === 'High/Low Scale') {
        const highOption = questionJSON.hiLo.highOption;

        if (_.isNil(benchmarkData.swapped) && is2023Survey && [5739, 5748].indexOf(benchmarkData.questionId) > -1) {
          const swap = (arr, x, y) => {
            const temp = arr[x];
            arr[x] = arr[y];
            arr[y] = temp;
          };
          swap(benchmarkData.AllClients.individualCounts, 2, 8);
          swap(benchmarkData.AllClients.individualCounts, 4, 6);
          benchmarkData.swapped = true;
          if (benchmarkData.winners.individualCounts) {
            swap(benchmarkData.winners.individualCounts, 2, 8);
            swap(benchmarkData.winners.individualCounts, 4, 6);
          }
        }

        all = benchmarkData.AllClients;
        winner = benchmarkData.winners;

        totalSum = 0;
        const allActKeys: number[] = [];
        Object.keys(all.individualCounts).forEach((key) => {
          if (questionJSON.treatAsScaled || trulyCande) {
            totalSum += ((parseInt(key, 10) / 2) * parseInt(all.individualCounts[key], 10));
          } else {
            const actKey = WidgetsCommon.getUnNormalizedVal(key, parseInt(highOption, 10));
            allActKeys.push(actKey);
            if (actKey) {
              totalSum += (actKey * parseInt(all.individualCounts[key], 10));
            }
          }
        });

        const tot = benchmarkData.AllClients.totalCount;
        if (tot !== 0) {
          benchmarkData.AllClientsAverage = parseFloat((totalSum / tot).toFixed(1));
          if (highOption !== 10 && !trulyCande) {
            benchmarkData.AllClientsAverage = ((benchmarkData.AllClientsAverage * highOption) / 10).toFixed(1);
          }
        } else {
          benchmarkData.AllClientsAverage = 0;
        }

        totalSum = 0;

        if (!_.isEmpty(winner)) {
          Object.keys(winner.individualCounts).forEach((key) => {
            if (questionJSON.treatAsScaled || trulyCande) {
              totalSum += ((parseInt(key, 10) / 2) * parseInt(winner.individualCounts[key], 10));
            } else {
              const actWinKey = WidgetsCommon.getUnNormalizedVal(key, parseInt(highOption, 10));
              if (actWinKey) {
                totalSum += (actWinKey * parseInt(winner.individualCounts[key], 10));
              }
            }
          });
          totWin = benchmarkData.winners.totalCount;
          benchmarkData.namWinnersBenchmark = parseFloat((totalSum / totWin).toFixed(1));
          if (highOption !== 10 && !trulyCande) {
            benchmarkData.namWinnersBenchmark = ((benchmarkData.namWinnersBenchmark * highOption) / 10).toFixed(1);
          }
        }

      } else if (questionJSON.questionType === 'Multiple Choice') {
        all = benchmarkData.AllClients;
        winner = benchmarkData.winners;
        totalSum = 0;

        Object.keys(all.individualCounts).forEach((key) => {
          const actKey = parseInt(key, 10);
          if (actKey) {
            totalSum += (actKey * parseInt(all.individualCounts[key], 10));
          }
        });

        const tot = benchmarkData.AllClients.totalCount;
        if (tot !== 0) {
          benchmarkData.AllClientsAverage = parseFloat((totalSum / tot).toFixed(1));
        } else {
          benchmarkData.AllClientsAverage = 0;
        }

        totalSum = 0;

        if (!_.isEmpty(winner)) {
          Object.keys(winner.individualCounts).forEach((key) => {
            const actWinKey = parseInt(key, 10);
            if (actWinKey) {
              totalSum += (actWinKey * parseInt(winner.individualCounts[key], 10));
            }
          });

          totWin = benchmarkData.winners.totalCount;

          benchmarkData.namWinnersBenchmark = parseFloat((totalSum / totWin).toFixed(1));
        }
      }

    }
  }

  /**
   * Get the numeric answers rating 10 pt scale
   * @param answers_i - The answers
   * @param scale - The scale
   * @returns { color: string; responses: number; score: number }
   */
  static getNumericAnswersRating10PtScale(answers_i: any, scale: number): { color: string; responses: number; score: number } {
    const totalRating = _.sum(_.map(answers_i, r => parseInt(r.key, 10) * r.doc_count));
    const totalResponses = _.sum(_.map(answers_i, 'doc_count'));
    const score = parseFloat((totalRating / totalResponses).toFixed(1));
    return {
      color: '#111',
      responses: totalResponses,
      score
    };
  }

  static async loadOrganizationBenchmark(caller: any): Promise<void> {
    const selectedSurveys = caller.widgetsCommon.getSelectedSurveysForQuestionWidget(caller.widgetData, caller.view);
    const questionType = caller.questionJson.questionType === SurveyQuestionTypes.NPS
      ? SurveyQuestionTypes.NPS
      : SurveyQuestionTypes.HiLowScale;
    const response: any = await firstValueFrom<any>(caller.surveysResultsService.getSurveyQuestionResponse(
      [], caller.surveyId, caller.employeeCampaignId, caller.widgetFilterKey, caller.stepIndex,
      caller.consolidatedView, caller.questionJson.questionId, questionType, caller.currentTimePeriod,
      caller.selectedChartDisplayOption.key, caller.parentSurveyId, selectedSurveys, undefined,
      caller.shareId, caller.dashboardId
    ));
    let score: any;
    if (questionType === SurveyQuestionTypes.NPS) {
      score = caller.getNPS(response.body.numericRatings);
    } else {
      const scale = caller.questionJson.hiLo?.labels?.length ?? 10;
      score = WidgetsCommon.getNumericAnswersRating(response.body.numericRatings, scale);
    }
    caller.benchmarkData = caller.benchmarkData || {};
    caller.benchmarkData.organizationBenchmark = score;
    caller.widgetData.benchmarkData = caller.benchmarkData;
  }

  /**
   * Handle the benchmark data
   * @param caller - The caller
   */
  static async handleBenchmarkData(caller: any): Promise<void> {
    try {
      if (caller.questionJson.questionType === 'Extracted Question-Multiple Choice') {
        caller.benchmarkData = {};
        return;
      }
      // console.log('caller.questionJson.questionType', caller.questionJson.questionType);
      caller.compareToQuestionId = _.get(caller.questionJson, 'relatedQuestion.questionId');
      if (caller.compareWithSiblingOptions.length > 0 || !_.isNil(caller.compareToQuestionId)) {
        try {
          const benchmarkData = await firstValueFrom<any>(caller.surveysResultsService.getBenchmarkData(
            caller.compareToQuestionId || caller.questionJson.questionId, caller.surveyId, caller.questionJson.tags,
            caller.questionJson.questionType, caller.currentTimePeriod));
          // TODO: This should be based on region
          // console.log('cande benchmarkData', benchmarkData);

          if (!_.isNil(_.get(benchmarkData, 'body.hits.hits'))) {
            const benchmarkDataRec = _.find(benchmarkData.body.hits.hits, (hit: any): any => !_.isNil(hit._source.winners));
            caller.benchmarkData = caller.benchmarkData || {};
            _.set(caller.benchmarkData, 'cande', _.get(benchmarkDataRec, '_source'));
            if (caller.benchmarkData.cande) {
              // TODO: find a better way to handle this.
              caller.compareWithSiblingOptions = ConfigService.getCandeCompareToOptions();

              _.each(['AllClients', 'winners'], (field: string) => {
                if (caller.benchmarkData.cande[field] && typeof caller.benchmarkData.cande[field] === 'string') {
                  caller.benchmarkData.cande[field] = JSON.parse(caller.benchmarkData.cande[field]);
                }
              });

              caller.widgetsCommon.calculateBenchmarkScores(
                benchmarkData.body.json || benchmarkData.body.questionJSON,
                caller.benchmarkData.cande,
                _.isNil(caller.compareToQuestionId),
                caller.parentSurveyId
              );


              caller.widgetData.benchmarkData = caller.widgetData.benchmarkData || {};
              caller.widgetData.benchmarkData.cande = caller.benchmarkData.cande;
            }
          }
        } catch (e) {
          console.log('caller.widgetData.questionId', caller.widgetData.questionId, caller.widgetData.title, caller.benchmarkData);
          // throw e;
        }
        // console.log(caller.questionJson.questionDisplayIndex, caller.questionJson.title, caller.benchmarkData);
      }
      // if (caller.questionJson.benchmark) {
      //   console.log(`caller.questionJson.benchmark ${caller.questionJson.benchmark} - ${caller.questionJson.title}`);
      // }
      let benchmark = null;
      if (ConfigService.config.benchmark2Enabled === 'yes') {
        benchmark = caller.questionJson.benchmark2;
        const unique = (source: string) =>
          Array.from(
            new Set(
              source
                .split(',')
                .map(s => s.split('~'))
                .reduce((acc, val) => acc.concat(val), [])
                .map(s => s.trim())
            )
          );
        if (typeof benchmark === 'string') {
          if (!_.isEmpty(benchmark.trim())) {
            benchmark = unique(benchmark.trim()).join('~');
          } else {
            benchmark = null;
          }

        } else if (_.isArray(benchmark)) {
          if (!_.isEmpty(benchmark)) {
            benchmark = unique(benchmark.join(',')).join('~');
          } else {
            benchmark = null;
          }
        }
      } else {
        benchmark = caller.questionJson.benchmark;
      }
      if (!_.isNil(benchmark)) {
        if (_.isNil(caller.survey) || _.isNil(caller.survey.surveyJSON)) {
          console.log('caller.survey.surveyJSON is nil', _.get(caller, 'survey.surveyJSON'));
          return;
        }
        if (caller.survey.surveyJSON.surveyModule !== 'EMPLOYEE') {
          const survaleBenchmark = await firstValueFrom<any>(caller.surveysResultsService.getSurvaleBenchmarks(
            benchmark, undefined, undefined, caller.questionJson.questionType, caller.currentTimePeriod
          ));
          // console.log('survaleBenchmark', survaleBenchmark);
          caller.widgetData.benchmarkData = caller.widgetData.benchmarkData || {};
          if (caller.questionJson.questionType === SurveyQuestionTypes.NPS) {
            // const npsScore: any = {
            //   promoters: _.get(survaleBenchmark, 'body.aggregations.nested_score.promoters.value'),
            //   detractors: _.get(survaleBenchmark, 'body.aggregations.nested_score.detractors.value'),
            //   passive: _.get(survaleBenchmark, 'body.aggregations.nested_score.passive.value'),
            // };
            caller.widgetData.benchmarkData.survale = _.get(survaleBenchmark, 'body');
            // caller.widgetData.benchmarkData.survale.nps = this.getNPS(npsScore);
            // caller.widgetData.benchmarkData.survale.records = _.get(survaleBenchmark, 'body.aggregations.nested_score.all.value');
          } else {
            caller.widgetData.benchmarkData.survale = _.get(survaleBenchmark, 'body');
            // caller.widgetData.benchmarkData.survale.score = parseFloat(_.get(survaleBenchmark, 'body.score'));
            // caller.widgetData.benchmarkData.survale.records = _.get(survaleBenchmark, 'body.total');
            let scale = 10;
            if (caller.widgetData.question.questionType === SurveyQuestionTypes.HiLowScale) {
              scale = caller.widgetData.question.hiLo.labels.length;
            } else {
              scale = caller.widgetData.question.answers.length;
            }
            _.keys(caller.widgetData.benchmarkData.survale).forEach(key => {
              if (key === 'primaryIndustry') {
                caller.widgetData.benchmarkData.survale[key].forEach(pi => { pi.score = pi.score * scale / 10; });
              } else {
                caller.widgetData.benchmarkData.survale[key].score = caller.widgetData.benchmarkData.survale[key].score * scale / 10;
              }
            });
          }
        } else {
          const response = await firstValueFrom<any>(caller.surveysResultsService.getEmployeeBenchmarks(
            caller.questionJson.benchmark, caller.questionJson.questionType, caller.currentTimePeriod
          ));
          const empBMs = response.body.employeeBMs;
          if (empBMs.length > 0) {
            caller.widgetData.benchmarkData = {};
            caller.widgetData.benchmarkData.emp = {};
            if (empBMs[0].scoreType === 'NPS') {
              caller.widgetData.benchmarkData.emp.nps = { nps: empBMs[0].score };
              caller.widgetData.benchmarkData.emp.source = empBMs[0].benchmarkSource;
            } else {
              if (caller.questionJson.questionType === 'High/Low Scale') {
                const val = WidgetsCommon.getUnNormalizedVal(Math.round(empBMs[0].score / 10), caller.questionJson.hiLo.highOption);
                caller.widgetData.benchmarkData.emp.score = val;
              } else {
                caller.widgetData.benchmarkData.emp.score = empBMs[0].score;
              }
              caller.widgetData.benchmarkData.emp.source = empBMs[0].benchmarkSource;
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Get the numeric answers rating
   * @param answers_i - The answers
   * @param scale - The scale
   * @returns { color: string; responses: number; score: number }
   */
  static getNumericAnswersRating(answers_i: any, scale: number): { color: string; responses: number; score: number } {
    const keyMap = {
      5: { 1: 1, 2: 3, 3: 5, 4: 7, 5: 10 },
      4: { 1: 3, 2: 5, 3: 8, 4: 10 },
      3: { 1: 3, 7: 2, 10: 3 }
    };
    const keyMapInverse = {
      5: { 1: 1, 3: 2, 5: 3, 7: 4, 10: 5 },
      4: { 3: 1, 5: 2, 8: 3, 10: 4 },
      // confirm with shash
      // 3: { 1: 2, 7: 2, 10: 3 }
      3: { 3: 1, 7: 2, 10: 3 }
    };
    const keyMapInverse2 = {
      5: { 2: 1, 4: 2, 6: 3, 8: 4 },
      // 4: {3: 1, 5: 2, 8: 3, 10: 4},
      3: { 1: 3, 6: 2, 10: 3 }
    };
    let totalRating;
    switch (scale) {
      case 10:
        totalRating = _.sum(_.map(answers_i, r => parseInt(r.key, 10) * r.doc_count));
        break;
      case 5:
      case 4:
      case 3:
        totalRating = _.sum(_.map(answers_i, r => {
          const mappedNum = keyMapInverse[scale][parseInt(r.key, 10)] || keyMapInverse2[scale][parseInt(r.key, 10)];
          return mappedNum ? mappedNum * r.doc_count : 0;
        }));
        break;
    }
    // console.log('totalRating', totalRating);
    // totalRating = _.sum(_.map(answers_i, r => parseInt(r.key, 10) * r.doc_count));
    const totalResponses = _.sum(_.map(answers_i, 'doc_count'));
    if (totalResponses > 0) {
      const score = parseFloat((totalRating / totalResponses).toFixed(1));
      return {
        color: '#111',
        responses: totalResponses,
        score
      };
    } else {
      return {
        color: '#111',
        responses: 0,
        score: 0
      };
    }

  }

  /**
   * Shorten the number
   * @param input - The input
   * @param args - The args
   * @returns string | null
   */
  static shortenNumber(input: any, args?: any): string | null {
    const suffixes = ['k', 'M', 'G', 'T', 'P', 'E'];
    if (Number.isNaN(input)) {
      return null;
    }
    if (input < 1000) {
      return input;
    }
    const exp = Math.floor(Math.log(input) / Math.log(1000));
    return (input / Math.pow(1000, exp)).toFixed(args) + suffixes[exp - 1];
  }


  // static setSize(chart, mySize, reservedHeightForHTMLContent) {
  //   if (chart && mySize) {
  //     chart.setSize(
  //       mySize.width, mySize.height - reservedHeightForHTMLContent
  //     )
  //   }
  // }
  //
  // static setSizeRepeatedly(chart, mySize, reservedHeightForHTMLContent) {
  //   setTimeout((): void => WidgetsCommon.setSize(chart, mySize, reservedHeightForHTMLContent), 500);
  //   setTimeout((): void => WidgetsCommon.setSize(chart, mySize, reservedHeightForHTMLContent), 1000);
  //   setTimeout((): void => WidgetsCommon.setSize(chart, mySize, reservedHeightForHTMLContent), 1500);
  //   // setTimeout((): void => console.log('this.mySize~~', this.mySize))
  // }


}
